package app

import app.api.*
import app.svg.logo
import app.svg.sun
import app.view.*
import dev.fritz2.core.alt
import dev.fritz2.core.render
import dev.fritz2.core.src
import dev.fritz2.routing.MapRouter
import io.ktor.client.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.serialization.json.Json

object Routes {
    const val PROJECTS = "projects"
    const val PROJECT_DETAIL = "projectDetail"
    const val PROJECT_STEP = "projectStep"
    const val PROJECT_VARIABLE = "projectVariable"
    const val COMPANIES = "companies"
    const val STAGES = "stages"
    const val DEPLOYABLES = "deployables"
    const val DEPLOYABLE = "deployable"
    const val RELEASES = "releases"
    const val RELEASE = "release"
    const val ROLLOUTS = "rollouts"
    const val ROLLOUT = "rollout"

    object Params {
        const val DEPLOYABLE_NAME = "deployableName"
        const val PROJECT_ID = "projectId"
        const val RELEASE_ID = "releaseId"
        const val ROLLOUT_ID = "rolloutId"
        const val SORT = "sort"
        const val ORDER = "order"
        const val PAGE = "page"
    }
}

object CustomRouter : MapRouter(mapOf("route" to Routes.PROJECTS), job = Job()) {
    val projects = handle { mapOf("route" to Routes.PROJECTS) }
    val companies = handle { mapOf("route" to Routes.COMPANIES) }
    val stages = handle { mapOf("route" to Routes.STAGES) }
    val deployables = handle { mapOf("route" to Routes.DEPLOYABLES) }
    val deployable = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.DEPLOYABLE,
            Routes.Params.DEPLOYABLE_NAME to (params[Routes.Params.DEPLOYABLE_NAME] ?: ""),
            Routes.Params.SORT to (params[Routes.Params.SORT] ?: "date"),
            Routes.Params.ORDER to (params[Routes.Params.ORDER] ?: "desc"),
            Routes.Params.PAGE to (params[Routes.Params.PAGE] ?: "1")
        )
    }
    val releases = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.RELEASES,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
            Routes.Params.SORT to (params[Routes.Params.SORT] ?: "date"),
            Routes.Params.ORDER to (params[Routes.Params.ORDER] ?: "desc"),
            Routes.Params.PAGE to (params[Routes.Params.PAGE] ?: "1")
        )
    }
    val release = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.RELEASE,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
            Routes.Params.RELEASE_ID to (params[Routes.Params.RELEASE_ID] ?: ""),
            Routes.Params.PAGE to (params[Routes.Params.PAGE] ?: "1"),
            Routes.Params.ORDER to (params[Routes.Params.ORDER] ?: "desc"),
        )
    }
    val rollouts = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.ROLLOUTS,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
            Routes.Params.SORT to (params[Routes.Params.SORT] ?: "date"),
            Routes.Params.ORDER to (params[Routes.Params.ORDER] ?: "desc"),
            Routes.Params.PAGE to (params[Routes.Params.PAGE] ?: "1")
        )
    }
    val rollout = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.ROLLOUT,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
            Routes.Params.ROLLOUT_ID to (params[Routes.Params.ROLLOUT_ID] ?: ""),
        )
    }
    val projectDetail = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.PROJECT_DETAIL,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
        )
    }
    val projectStep = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.PROJECT_STEP,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
        )
    }
    val projectVariables = handle<Map<String, String>> { _, params ->
        mapOf(
            "route" to Routes.PROJECT_VARIABLE,
            Routes.Params.PROJECT_ID to (params[Routes.Params.PROJECT_ID] ?: ""),
        )
    }
}

val client = HttpClient(Js) {
    install(Resources)
    install(ContentNegotiation) {
        json(Json {
            ignoreUnknownKeys = true
            prettyPrint = true
        })
    }
    expectSuccess = false
    HttpResponseValidator {
        validateResponse { response ->
            if (!response.status.isSuccess() && response.status != HttpStatusCode.Forbidden) {
                window.alert("Http Request failed: ${response.status} ${response.bodyAsText()}")
            }
            if (response.status == HttpStatusCode.Forbidden) {
                window.location.href = "/api/login"
            }
        }
    }
}

fun main() {
    val projectsApi = ProjectsApi()
    val projectApi = ProjectApi()
    val projectVariablesApi = ProjectVariablesApi()
    val projectStepApi = ProjectStepApi()
    val companiesApi = CompaniesApi()
    val stagesApi = StagesApi()
    val deployablesApi = DeployablesApi()
    val deployablePaginatedApi = DeployablePaginatedApi()
    val projectReleasesPaginatedApi = ProjectReleasesPaginatedApi()
    val projectReleaseApi = ProjectReleaseApi()
    val rolloutExecutionApi = RolloutExecutionApi()
    val rolloutExecutionPaginatedApi = RolloutExecutionPaginatedApi()
    val rolloutExecutionsPaginatedApi = RolloutExecutionsPaginatedApi()

    val theme = window.localStorage.getItem("theme")
    if (theme == "dark") {
        window.document.documentElement?.classList?.add("dark")
    } else {
        window.document.documentElement?.classList?.remove("dark")
    }

    render {
        section("dark:bg-gray-900 min-h-screen") {
            header("border dark:border-gray-700 dark:bg-gray-800") {
                div("flex items-center justify-center w-full mx-auto max-w-6xl dark:text-white") {
                    logo("ml-3 h-8 dark:text-white")
                    CustomRouter.select(key = "route").render { (page, _) ->
                        nav("w-full h-16 flex justify-center items-center gap-4") {
                            a("cursor-pointer hover:underline dark:text-gray-200 ${if (page == Routes.PROJECTS) "underline" else ""}") {
                                +"Projects"
                                clicks handledBy CustomRouter.projects
                            }
                            a("cursor-pointer hover:underline dark:text-gray-200 ${if (page == Routes.COMPANIES) "underline" else ""}") {
                                +"Companies"
                                clicks handledBy CustomRouter.companies
                            }
                            a("cursor-pointer hover:underline dark:text-gray-200 ${if (page == Routes.STAGES) "underline" else ""}") {
                                +"Stages"
                                clicks handledBy CustomRouter.stages
                            }
                            a("cursor-pointer hover:underline dark:text-gray-200 ${if (page == Routes.DEPLOYABLES) "underline" else ""}") {
                                +"Deployables"
                                clicks handledBy CustomRouter.deployables
                            }
                        }
                    }
                    button("mr-4 ml-20") {
                        sun("w-8 h-8")
                        clicks handledBy {
                            val htmlElement = window.document.documentElement
                            val isDark = htmlElement?.classList?.contains("dark")

                            if (isDark == true) {
                                htmlElement.classList.remove("dark")
                                window.localStorage.setItem("theme", "light")
                            } else {
                                htmlElement?.classList?.add("dark")
                                window.localStorage.setItem("theme", "dark")
                            }
                        }
                    }
                }
            }
            main("w-full max-w-6xl mx-auto") {
                CustomRouter.select(key = "route").render { (page, other) ->
                    when (page) {
                        Routes.PROJECTS -> projectsView(projectsApi, companiesApi, stagesApi)
                        Routes.PROJECT_DETAIL -> projectDetailView(
                            projectApi,
                            other[Routes.Params.PROJECT_ID],
                        )

                        Routes.PROJECT_STEP -> projectStepView(
                            projectApi,
                            projectStepApi,
                            other[Routes.Params.PROJECT_ID],
                        )

                        Routes.PROJECT_VARIABLE -> projectVariablesView(
                            projectVariablesApi,
                            companiesApi,
                            stagesApi,
                            other[Routes.Params.PROJECT_ID],
                        )

                        Routes.COMPANIES -> companiesView(companiesApi)
                        Routes.DEPLOYABLES -> deployablesView(deployablesApi)
                        Routes.DEPLOYABLE -> deployablePaginatedView(
                            deployablePaginatedApi,
                            other[Routes.Params.DEPLOYABLE_NAME],
                            other[Routes.Params.PAGE]?.toIntOrNull() ?: 1,
                            other[Routes.Params.SORT] ?: "date",
                            other[Routes.Params.ORDER] ?: "desc",
                        )

                        Routes.RELEASES -> projectReleasesPaginatedView(
                            projectStepApi,
                            projectReleasesPaginatedApi,
                            deployablesApi,
                            other[Routes.Params.PROJECT_ID],
                            other[Routes.Params.PAGE]?.toIntOrNull() ?: 1,
                            other[Routes.Params.SORT] ?: "date",
                            other[Routes.Params.ORDER] ?: "desc",
                        )

                        Routes.RELEASE -> projectRelease(
                            projectApi,
                            projectReleaseApi,
                            rolloutExecutionPaginatedApi,
                            other[Routes.Params.PROJECT_ID],
                            other[Routes.Params.RELEASE_ID],
                            other[Routes.Params.PAGE]?.toIntOrNull() ?: 1,
                            other[Routes.Params.ORDER] ?: "desc",
                        )

                        Routes.ROLLOUTS -> rolloutExecutions(
                            rolloutExecutionsPaginatedApi,
                            other[Routes.Params.PROJECT_ID],
                            other[Routes.Params.PAGE]?.toIntOrNull() ?: 1,
                            other[Routes.Params.SORT] ?: "date",
                            other[Routes.Params.ORDER] ?: "desc",
                        )

                        Routes.ROLLOUT -> rolloutExecution(
                            rolloutExecutionApi,
                            other[Routes.Params.PROJECT_ID],
                            other[Routes.Params.ROLLOUT_ID],
                        )

                        Routes.STAGES -> stagesView(stagesApi)
                        else -> div { +"404 not found" }
                    }
                }
            }
        }
    }
}
